import { PASSWORD_MIN_LENGTH } from '@tomo/core/api';
import { Button, Dialog, Field, Input, LogoMark, toast } from '@tomo/ui';
import { Lock, Mail, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api, ApiRequestError } from '../../data/cloud/api.ts';
import { sendsEmail, signIn, signUp, useCloud } from '../../data/cloud/session.ts';

export type AuthMode = 'signin' | 'signup' | 'forgot';

const TITLES: Record<AuthMode, string> = {
  signin: 'Sign in to Tomo',
  signup: 'Create your Tomo account',
  forgot: 'Reset your password',
};

const DESCRIPTIONS: Record<AuthMode, string> = {
  signin: 'Sign in to sync your projects and collaborate with your team.',
  signup: 'Accounts are free. Upgrade a workspace to Pro for sync, real-time editing and teammates.',
  forgot: 'Enter the email you signed up with, and we’ll send you a link to choose a new password.',
};

function describeError(e: unknown, mode: AuthMode): string {
  if (!(e instanceof ApiRequestError)) return String(e);
  if (e.status === 0) return 'Tomo Cloud is not reachable right now.';
  if (mode !== 'forgot') {
    if (e.status === 401) return 'Email or password is incorrect.';
    if (e.status === 409) return 'An account with this email already exists. Try signing in.';
    if (e.status === 429) return 'Too many attempts. Wait a few minutes and try again.';
  }
  return e.message;
}

export function AuthDialog({ mode, onClose, onModeChange, onDone }: { mode: AuthMode | null; onClose: () => void; onModeChange: (m: AuthMode) => void; onDone?: () => void }) {
  const cloud = useCloud();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** The address a reset link was requested for. */
  const [sentTo, setSentTo] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setError(null);
    setSentTo(null);
  }, [mode]);

  const submit = async () => {
    if (!mode || busy) return;
    setError(null);
    if (mode === 'signup' && password.length < PASSWORD_MIN_LENGTH) {
      setError(`Use at least ${PASSWORD_MIN_LENGTH} characters for your password.`);
      return;
    }
    setBusy(true);
    try {
      if (mode === 'forgot') {
        await api.forgotPassword(email.trim());
        setSentTo(email.trim());
        return;
      }
      if (mode === 'signup') await signUp(email.trim(), password, name.trim() || email.split('@')[0]!);
      else await signIn(email.trim(), password);
      toast.success(mode === 'signup' ? 'Account created — welcome to Tomo!' : 'Signed in');
      onClose();
      onDone?.();
    } catch (e) {
      setError(describeError(e, mode));
    } finally {
      setBusy(false);
    }
  };

  const unavailable = cloud.status === 'unavailable';
  const switchTo = (next: AuthMode) => {
    onModeChange(next);
    // The link that was clicked may not exist in the next mode, so start its form at the first field.
    requestAnimationFrame(() => (next === 'signup' ? nameRef : emailRef).current?.focus());
  };
  const link = (label: string, to: AuthMode) => (
    <button type="button" className="inline-link" onClick={() => switchTo(to)}>
      {label}
    </button>
  );

  return (
    <Dialog
      open={mode !== null}
      onOpenChange={(o) => !o && onClose()}
      size="sm"
      icon={<LogoMark size={22} />}
      title={TITLES[mode ?? 'signin']}
      description={DESCRIPTIONS[mode ?? 'signin']}
    >
      {unavailable ? (
        <p className="muted" style={{ fontSize: 13 }}>
          Tomo Cloud isn’t reachable from this copy of the app. Everything still works locally — your projects are saved on this device.
        </p>
      ) : mode === 'forgot' && sentTo ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <p className="muted" style={{ fontSize: 13 }}>
            If {sentTo} has a Tomo account, a link to reset its password is on its way. It works for 1 hour.
          </p>
          <Button variant="primary" size="lg" block onClick={() => switchTo('signin')}>
            Back to sign in
          </Button>
        </div>
      ) : (
        <form
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          {mode === 'signup' && (
            <Field label="Name" htmlFor="auth-name">
              <Input ref={nameRef} id="auth-name" leading={<User />} autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ada Lovelace" autoFocus />
            </Field>
          )}
          <Field label="Email" htmlFor="auth-email" error={mode === 'forgot' ? error : undefined}>
            <Input
              ref={emailRef}
              id="auth-email"
              type="email"
              leading={<Mail />}
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@studio.com"
              autoFocus={mode !== 'signup'}
              invalid={mode === 'forgot' && !!error}
            />
          </Field>
          {mode !== 'forgot' && (
            <Field
              label="Password"
              htmlFor="auth-password"
              aside={mode === 'signin' && sendsEmail(cloud) ? link('Forgot password?', 'forgot') : undefined}
              hint={mode === 'signup' ? `At least ${PASSWORD_MIN_LENGTH} characters.` : undefined}
              error={error}
            >
              <Input
                id="auth-password"
                type="password"
                leading={<Lock />}
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                invalid={!!error}
              />
            </Field>
          )}
          <Button type="submit" variant="primary" size="lg" block loading={busy} style={{ marginTop: 4 }}>
            {mode === 'signup' ? 'Create account' : mode === 'forgot' ? 'Send reset link' : 'Sign in'}
          </Button>
          <p className="subtle" style={{ fontSize: 12.5, textAlign: 'center' }}>
            {mode === 'signup' ? (
              <>Already have an account? {link('Sign in', 'signin')}</>
            ) : mode === 'forgot' ? (
              link('Back to sign in', 'signin')
            ) : (
              <>New to Tomo? {link('Create an account', 'signup')}</>
            )}
          </p>
        </form>
      )}
    </Dialog>
  );
}
